import { COUNTRIES } from "@helpers/coutries";
import {
  AssignedOffice,
  AssignedOfficeColumn,
  Banks,
  BanksColumn,
  Cities,
  CitiesColumn,
  Commodities,
  CommoditiesColumn,
  CustomsOffice,
  CustomsOfficeColumn,
  Vessel,
  VesselColumn,
  Warehouses,
  WarehousesColumn,
  Terminals,
  TerminalsColumn,
  Truck,
  TruckColumn,
  Ports,
  PortsColumn,
  PaymentTerms,
  PaymentTermsColumn,
  Package,
  PackageColumn,
  Suppliers,
  SuppliersColumn,
  OptionType,
  Services,
  ServicesColumns,
  ServiceMaster,
  ServiceMasterColumn,
  Customer,
  CustomerColumn,
  PriceServiceType,
  ProductionMethodColumn,
  ProductionMethod,
} from "../types";
import {
  COMPANY_TYPE_OVERLAP,
  ROUTES,
  FLOW_OF_GOODS,
  SERVICE_TYPE,
  ROLE,
  MACRO_CATEGORY,
  BOOL,
} from "../config";
import { APL_ENTITY_CONFIG, PPL_ENTITY_CONFIG } from "./price-service-config";

export type ColumnConfig =
  | string
  | {
      field: string;
      headerName: string;
      sortable?: boolean;
      path?: string;
      sortField?: string;
    };

export type SettingsEntityRowMap = {
  offices: AssignedOffice;
  customs_offices: CustomsOffice;
  warehouses: Warehouses;
  customers: Customer;
  cities: Cities;
  vessels: Vessel;
  banks: Banks;
  commodities: Commodities;
  commodity_groups: Commodities;
  terminals: Terminals;
  truck: Truck;
  ports: Ports;
  terms: PaymentTerms;
  packages: Package;
  services: Services;
  suppliers: Suppliers;
  service_master: ServiceMaster;
  production_method: ProductionMethod;
  apl: PriceServiceType;
  ppl: PriceServiceType;
};

export type SettingsEntityKey = keyof SettingsEntityRowMap;

type InitialValue =
  | { id: number; label: string }
  | { id: number; label: string }[]
  | null;

export type InitialValueMapper<Row> = (row: Row) => InitialValue;

export type SettingsEntityConfigBase<Row> = {
  label: string;
  item?: string;
  path: string;
  endpoints: {
    list: string;
    getOne: (id: number | string) => string;
    create: string;
    update: (id: number | string) => string;
    delete?: (id: number | string) => string;
    restore?: (id: number | string) => string;
  };
  cellWidth?: number;
  columns: ColumnConfig[];
  fields: Array<{
    field: string;
    filterField?: string;
    disabled?: boolean;
    label: string;
    required?: boolean;
    multi?: boolean;
    api?: string;
    filter?: boolean;
    create?: boolean;
    view?: boolean;
    update?: boolean;
    extraParams?: Record<string, any>;
    control:
      | "textfield"
      | "autocomplete"
      | "checkbox"
      | "dynamic"
      | "date"
      | "groupped";
    range?: boolean;
    multiline?: boolean;
    validation?: string | string[];
    dependency?: string;
    dependencyParam?: string;
    path?: string;
    size?: number;
    mapper?: string;
    option?: string;
    section?: string;
    selectOption?: boolean;
    valueKey?: string;
    applyCleaning?: boolean;
    options?:
      | { label: string; id: string | number | boolean }[]
      | string[]
      | OptionType[];
    initialValueMapper?: InitialValueMapper<Row>;
  }>;
};

export type SettingsEntityConfigByKey<K extends SettingsEntityKey> = {
  key: K;
} & SettingsEntityConfigBase<SettingsEntityRowMap[K]>;

export const SETTINGS_ENTITIES: {
  [K in SettingsEntityKey]: SettingsEntityConfigByKey<K>;
} = {
  offices: {
    key: "offices",
    label: "Assigned Offices",
    item: "Assigned Office",
    path: "assigned-offices",
    cellWidth: 250,
    endpoints: {
      list: "/api/offices?show_all=true",
      getOne: (id) => `/api/offices/${id}`,
      create: "/api/offices",
      update: (id) => `/api/offices/${id}`,
      delete: (id) => `/api/offices/${id}`,
      restore: (id) => `/api/offices/${id}/restore`,
    },
    columns: [
      { field: AssignedOfficeColumn.ID, headerName: "Id", sortable: true },
      { field: AssignedOfficeColumn.NAME, headerName: "Name", sortable: true },
      {
        field: AssignedOfficeColumn.COUNTRY,
        headerName: "Country",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DELIVERY_EMAIL,
        headerName: "Delivery e-mail",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.ABBREVIATION,
        headerName: "Abbreviation",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DEFERRED_ACCOUNT_DPO_NUMBER,
        headerName: "Deferred Account DPO Number",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DPO_VAT_VALUE_EURO,
        headerName: "DPO VAT value Euro",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DPO_DUTY_VALUE_EURO,
        headerName: "DPO Duty value Euro",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DPO_EXPIRE_DATE,
        headerName: "DPO Expire Date",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.EMAIL,
        headerName: "E-mail",
        sortable: true,
      },
      {
        field: AssignedOfficeColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "abbreviation",
        label: "Abbreviation",
        control: "textfield",
        required: true,
        validation: ["uppercase", "max-5", "min-2", "alphanumeric"],
      },
      {
        field: "country",
        label: "Country",
        control: "autocomplete",
        options: COUNTRIES,
        required: true,
      },
      {
        field: "delivery_email",
        label: "Delivery e-mail",
        control: "textfield",
        validation: ["email"],
        required: true,
        filter: true,
      },
      {
        field: "deferred_account_dpo_number",
        label: "Deferred Account DPO Number",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "dpo_vat_value_euro",
        label: "DPO VAT value Euro",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "dpo_duty_value_euro",
        label: "DPO Duty value Euro",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "dpo_expire_date",
        label: "DPO Expire Date",
        control: "date",
        required: false,
        filter: true,
        range: true,
      },
      {
        field: "email",
        label: "E-mail",
        control: "textfield",
        validation: ["email"],
        required: false,
        filter: true,
      },
    ],
  },
  customs_offices: {
    key: "customs_offices",
    label: "Customs Offices",
    item: "Customs Office",
    path: "customs-offices",
    endpoints: {
      list: "/api/customs-offices?show_all=true",
      getOne: (id) => `/api/customs-offices/${id}`,
      create: "/api/customs-offices",
      update: (id) => `/api/customs-offices/${id}`,
      delete: (id) => `/api/customs-offices/${id}`,
      restore: (id) => `/api/customs-offices/${id}/restore`,
    },
    columns: [
      { field: CustomsOfficeColumn.ID, headerName: "Id", sortable: true },
      { field: CustomsOfficeColumn.CODE, headerName: "Code", sortable: true },
      {
        field: CustomsOfficeColumn.NOTE,
        headerName: "Note",
        sortable: true,
        sortField: "note",
      },
      {
        field: CustomsOfficeColumn.TITLE,
        headerName: "Title",
        sortable: false,
      },
      {
        field: CustomsOfficeColumn.CITY,
        headerName: "City",
        path: "city.name",
        sortable: true,
        sortField: "city__name",
      },
      {
        field: CustomsOfficeColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "code",
        label: "Code",
        control: "textfield",
        required: true,
        filter: true,
      },
      {
        field: "note",
        label: "Note",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "title",
        label: "Title",
        control: "textfield",
        required: false,
        create: false,
        filter: false,
      },
      {
        field: "city",
        label: "City",
        control: "dynamic",
        api: "/api/cities?show_all=true",
        path: "city.name",
        required: true,
        filter: true,
        filterField: "city__id",
      },
    ],
  },
  warehouses: {
    key: "warehouses",
    label: "Warehouses",
    item: "Warehouse",
    path: "warehouses",
    cellWidth: 250,
    endpoints: {
      list: "/api/warehouses?show_all=true",
      getOne: (id) => `/api/warehouses/${id}`,
      create: "/api/warehouses",
      update: (id) => `/api/warehouses/${id}`,
      delete: (id) => `/api/warehouses/${id}`,
      restore: (id) => `/api/warehouses/${id}/restore`,
    },
    columns: [
      { field: WarehousesColumn.ID, headerName: "Id", sortable: true },
      {
        field: WarehousesColumn.PORT,
        headerName: "Port",
        sortable: true,
        sortField: "port__name",
        path: "port.name",
      },
      {
        field: WarehousesColumn.SUPPLIER,
        headerName: "Supplier",
        sortable: true,
        sortField: "supplier__name",
        path: "supplier.name",
      },
      { field: WarehousesColumn.NAME, headerName: "Name", sortable: true },
      {
        field: WarehousesColumn.ADDRESS,
        headerName: "Loading Address",
        sortable: true,
      },
      {
        field: WarehousesColumn.POST_CODE,
        headerName: "Loading Address Post Code",
        path: "city.post_code",
        sortable: true,
        sortField: "city__post_code",
      },
      {
        field: WarehousesColumn.CITY,
        headerName: "Loading Address City",
        path: "city.name",
        sortable: true,
        sortField: "city__name",
      },
      {
        field: WarehousesColumn.PROVINCE,
        headerName: "Loading Address Province",
        path: "city.province",
        sortable: true,
        sortField: "city__province",
      },
      {
        field: WarehousesColumn.COUNTRY,
        headerName: "Loading Address Country",
        path: "city.country",
        sortable: true,
        sortField: "city__country",
      },
      {
        field: WarehousesColumn.HOURS,
        headerName: "Opening Hours",
        sortable: true,
      },
      {
        field: WarehousesColumn.FISCAL_WAREHOUSE,
        headerName: "Fiscal Warehouse",
      },
      {
        field: WarehousesColumn.FISCAL_WAREHOUSE_CODE,
        headerName: "Fiscal Warehouse Code",
        sortable: true,
      },
      {
        field: WarehousesColumn.BONDED_WAREHOUSE,
        headerName: "Bonded Warehouse",
      },
      {
        field: WarehousesColumn.BONDED_WAREHOUSE_CODE,
        headerName: "Bonded Warehouse Code",
        sortable: true,
      },
      {
        field: WarehousesColumn.AUTHORIZATION_NUMBER_H2,
        headerName: "Authorization Number (H2)",
        sortable: true,
      },
      {
        field: WarehousesColumn.CUSTOMS_DECISION_H2,
        headerName: "Customs decision number (H2)",
        sortable: true,
      },
      {
        field: WarehousesColumn.GUARANTEE_A11_TER_H2,
        headerName: "Guarantee A11/TER (H2)",
        sortable: true,
      },
      {
        field: WarehousesColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "port",
        label: "Port",
        control: "dynamic",
        api: "/api/ports",
        path: "port.name",
        filter: false,
        required: true,
      },
      {
        field: "supplier",
        label: "Supplier",
        control: "dynamic",
        api: "api/suppliers/names",
        path: "supplier.name",
        filter: false,
      },
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "address",
        label: "Loading Address",
        control: "textfield",
        required: true,
      },
      {
        field: "post_code",
        label: "Loading Address Post code",
        control: "textfield",
        path: "city.post_code",
        filter: false,
        create: false,
      },
      {
        field: "city",
        label: "Loading Address City",
        control: "dynamic",
        api: "/api/cities?show_all=true",
        path: "city.name",
        required: true,
        filter: false,
      },
      {
        field: "city__province",
        label: "Loading Address Province",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "city__country",
        label: "Loading Address Country",
        control: "autocomplete",
        options: COUNTRIES,
        create: false,
        view: false,
      },
      {
        field: "opening_hours",
        label: "Opening Hours",
        control: "textfield",
      },
      {
        field: "fiscal_warehouse_code",
        label: "Fiscal Warehouse Code",
        control: "textfield",
        dependency: "fiscal_warehouse",
      },
      {
        field: "bonded_warehouse_code",
        label: "Bonded Warehouse Code",
        control: "textfield",
        dependency: "fiscal_warehouse",
      },
      {
        field: "authorization_number_h2",
        label: "Authorization Number (H2)",
        control: "textfield",
        required: false,
      },
      {
        field: "customs_decision_h2",
        label: "Customs decision number (H2)",
        control: "textfield",
        required: false,
      },
      {
        field: "guarantee_a11_ter_h2",
        label: "Guarantee A11/TER (H2)",
        control: "textfield",
        required: false,
      },
      {
        field: "port",
        label: "Port",
        control: "dynamic",
        api: "/api/ports?show_all=true",
        create: false,
        view: false,
        multi: true,
      },
      {
        field: "supplier",
        label: "Supplier",
        control: "dynamic",
        api: "api/suppliers/names?show_all=true",
        create: false,
        view: false,
        multi: true,
      },
      {
        field: "country",
        label: "Loading Address Country",
        control: "textfield",
        path: "city.country",
        filter: false,
        create: false,
      },
      {
        field: "province",
        label: "Loading Address Province",
        control: "textfield",
        path: "city.province",
        filter: false,
        create: false,
      },
      {
        field: "city__name",
        label: "Loading Address City",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "city__post_code",
        label: "Loading Address Post Code",
        control: "textfield",
        create: false,
        view: false,
      },
    ],
  },
  customers: {
    key: "customers",
    label: "Customers",
    item: "Customer",
    path: "customers",
    cellWidth: 250,
    endpoints: {
      list: "/api/customers?show_all=true",
      getOne: (id) => `/api/customers/${id}`,
      create: "/api/customers",
      update: (id) => `/api/customers/${id}`,
      delete: (id) => `/api/customers/${id}`,
      restore: (id) => `/api/customers/${id}/restore`,
    },
    columns: [
      { field: CustomerColumn.ID, headerName: "Id", sortable: true },
      {
        field: CustomerColumn.NAME,
        headerName: "Customer name",
        sortable: true,
      },
      {
        field: CustomerColumn.SEAWAY_REFERENCE,
        headerName: "Seaway internal reference G7",
        sortable: true,
      },
      {
        field: CustomerColumn.TERMS_OF_PAYMENT,
        headerName: "Terms of payment",
        sortable: true,
        sortField: "terms_of_payment__name",
        path: "terms_of_payment.name",
      },
      {
        field: CustomerColumn.COMMERCIAL_EMAIL,
        headerName: "Commercial Email Address",
        sortable: false,
      },
      {
        field: CustomerColumn.OPERATIVE_EMAIL,
        headerName: "Operative Email Address",
        sortable: false,
      },
      {
        field: CustomerColumn.ACCOUNTING_EMAIL,
        headerName: "Accounting Email Address",
        sortable: false,
      },
      {
        field: CustomerColumn.LEGAL_ADDRESS,
        headerName: "Legal Address",
        sortable: true,
      },
      {
        field: CustomerColumn.LEGAL_ADDRESS_CITY,
        headerName: "Legal Address City",
        sortable: true,
        sortField: "legal_city__name",
        path: "legal_city.name",
      },
      {
        field: CustomerColumn.LEGAL_ADDRESS_PROVINCE,
        headerName: "Legal Address Province",
        sortable: true,
        sortField: "legal_city__province",
        path: "legal_city.province",
      },
      {
        field: CustomerColumn.LEGAL_ADDRESS_COUNTRY,
        headerName: "Legal Address Country",
        sortable: true,
        sortField: "legal_city__country",
        path: "legal_city.country",
      },
      {
        field: CustomerColumn.LEGAL_ADDRESS_POST_CODE,
        headerName: "Legal Address Post Code",
        sortable: true,
        sortField: "legal_city__post_code",
        path: "legal_city.post_code",
      },
      {
        field: CustomerColumn.CUSTOMS_REPRESENTATIVE_MANDATE,
        headerName: "Customs Representative Mandate",
        sortable: true,
      },
      {
        field: CustomerColumn.VALIDITY_DATE,
        headerName: "Validity Date",
        sortable: true,
      },
      {
        field: CustomerColumn.VAT_NUMBER,
        headerName: "VAT Number",
        sortable: true,
      },
      {
        field: CustomerColumn.CBAM_AUTHORIZATION_NUMBER,
        headerName: "CBAM Authorization Number",
        sortable: true,
      },
      {
        field: CustomerColumn.DESTINATION_CUSTOMS_OFFICE,
        headerName: "Destination Customs Office",
        sortable: true,
      },
      {
        field: CustomerColumn.CUSTOMS_DECISION_H4,
        headerName: "Customs Decision H4",
        sortable: true,
      },
      {
        field: CustomerColumn.GUARANTEE_A11_TER_H4,
        headerName: "Guarantee A11/TER (H4)",
        sortable: true,
      },
      {
        field: CustomerColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "name",
        label: "Customer name",
        control: "textfield",
        required: true,
        filter: true,
      },
      {
        field: "seaway_reference",
        label: "Seaway internal reference G7",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "terms_of_payment",
        filterField: "terms_of_payment__name",
        label: "Terms of payment",
        control: "dynamic",
        api: "/api/terms-of-payment?show_all=true",
        path: "terms_of_payment.name",
        required: true,
        filter: true,
        valueKey: "label",
      },
      {
        field: "legal_address",
        label: "Legal Address",
        control: "textfield",
        required: true,
        filter: true,
      },
      {
        field: "legal_city",
        filterField: "legal_city__name",
        label: "Legal Address City",
        control: "dynamic",
        api: "/api/cities?show_all=true",
        path: "legal_city.name",
        required: true,
        filter: true,
        valueKey: "label",
      },
      {
        field: "legal_city__province",
        label: "Legal Address Province",
        control: "textfield",
        path: "legal_city.province",
        filter: true,
        create: false,
      },
      {
        field: "legal_city__country",
        label: "Legal Address Country",
        control: "autocomplete",
        path: "legal_city.country",
        options: COUNTRIES,
        filter: true,
        create: false,
      },
      {
        field: "legal_city__post_code",
        label: "Legal Address Post Code",
        control: "textfield",
        path: "legal_city.post_code",
        filter: true,
        create: false,
      },
      {
        field: "customs_representative_mandate",
        label: "Customs Representative Mandate",
        control: "autocomplete",
        options: BOOL,
        filter: true,
      },
      {
        field: "validity_date",
        label: "Validity Date",
        control: "date",
        filter: true,
        range: true,
      },
      {
        field: "vat_number",
        label: "VAT Number",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "cbam_authorization_number",
        label: "CBAM Authorization Number",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "destination_customs_office",
        label: "Destination Customs Office",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "customs_decision_h4",
        label: "Customs Decision H4",
        control: "textfield",
        required: false,
        filter: true,
      },
      {
        field: "guarantee_a11_ter_h4",
        label: "Guarantee A11/TER (H4)",
        control: "textfield",
        required: false,
        filter: true,
      },
    ],
  },
  cities: {
    key: "cities",
    label: "Cities",
    item: "City",
    path: "cities",
    endpoints: {
      list: "/api/cities?show_all=true",
      getOne: (id) => `/api/cities/${id}`,
      create: "/api/cities",
      update: (id) => `/api/cities/${id}`,
      delete: (id) => `/api/cities/${id}`,
      restore: (id) => `/api/cities/${id}/restore`,
    },
    columns: [
      { field: CitiesColumn.ID, headerName: "Id", sortable: true },
      { field: CitiesColumn.NAME, headerName: "Name", sortable: true },
      {
        field: CitiesColumn.POST_CODE,
        headerName: "Post code",
        sortable: true,
      },
      { field: CitiesColumn.PROVINCE, headerName: "Province", sortable: true },
      { field: CitiesColumn.COUNTRY, headerName: "Country", sortable: true },
      {
        field: CitiesColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "name",
        label: "Name",
        control: "textfield",
        required: true,
      },
      {
        field: "post_code",
        label: "Post Code",
        control: "textfield",
        required: true,
      },
      {
        field: "province",
        label: "Province",
        control: "textfield",
        required: true,
      },
      {
        field: "country",
        label: "Country",
        control: "autocomplete",
        options: COUNTRIES,
        required: true,
      },
    ],
  },
  vessels: {
    key: "vessels",
    label: "Vessels",
    item: "Vessel",
    path: "vessels",
    endpoints: {
      list: "/api/vessels?show_all=true",
      getOne: (id) => `/api/vessels/${id}`,
      create: "/api/vessels",
      update: (id) => `/api/vessels/${id}`,
      delete: (id) => `/api/vessels/${id}`,
      restore: (id) => `/api/vessels/${id}/restore`,
    },
    columns: [
      { field: VesselColumn.ID, headerName: "Id", sortable: true },
      { field: VesselColumn.NAME, headerName: "Name", sortable: true },
      { field: VesselColumn.TYPE, headerName: "Type", sortable: true },
      { field: VesselColumn.IMO, headerName: "IMO", sortable: true },
      {
        field: VesselColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      { field: "type", label: "Type", control: "textfield", required: true },
      { field: "imo", label: "IMO", control: "textfield", required: true },
    ],
  },
  banks: {
    key: "banks",
    label: "Banks",
    item: "Bank",
    path: "banks",
    endpoints: {
      list: "/api/banks?show_all=true",
      getOne: (id) => `/api/banks/${id}`,
      create: "/api/banks",
      update: (id) => `/api/banks/${id}`,
      delete: (id) => `/api/banks/${id}`,
      restore: (id) => `/api/banks/${id}/restore`,
    },
    columns: [
      { field: BanksColumn.ID, headerName: "Id", sortable: true },
      { field: BanksColumn.NAME, headerName: "Name", sortable: true },
      { field: BanksColumn.ADDRESS, headerName: "Address", sortable: true },
      {
        field: BanksColumn.CITY,
        headerName: "City",
        path: "city.name",
        sortable: true,
        sortField: "city__name",
      },
      {
        field: BanksColumn.COUNTRY,
        headerName: "Country",
        path: "city.country",
        sortable: true,
        sortField: "city__country",
      },
      {
        field: BanksColumn.POST_CODE,
        headerName: "Post Code",
        path: "city.post_code",
        sortable: true,
        sortField: "city__post_code",
      },
      {
        field: BanksColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "address",
        label: "Address",
        control: "textfield",
      },
      {
        field: "city",
        label: "City",
        control: "dynamic",
        api: "/api/cities",
        path: "city.name",
        filter: false,
      },
      {
        field: "city__name",
        label: "City",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "city__country",
        label: "Country",
        control: "autocomplete",
        options: COUNTRIES,
        create: false,
        view: false,
      },
      {
        field: "country",
        label: "Country",
        control: "textfield",
        path: "city.country",
        filter: false,
        create: false,
      },
      {
        field: "post_code",
        label: "Post code",
        control: "textfield",
        path: "city.post_code",
        filter: false,
        create: false,
      },
      {
        field: "city__post_code",
        label: "Post code",
        control: "textfield",
        create: false,
        view: false,
      },
    ],
  },
  commodities: {
    key: "commodities",
    label: "Commodities",
    item: "Commodity",
    path: "commodities",
    endpoints: {
      list: "/api/commodities?show_all=true&parent_id__isnull=false",
      getOne: (id) => `/api/commodities/${id}`,
      create: "/api/commodities",
      update: (id) => `/api/commodities/${id}`,
      delete: (id) => `/api/commodities/${id}`,
      restore: (id) => `/api/commodities/${id}/restore`,
    },
    columns: [
      { field: CommoditiesColumn.ID, headerName: "Id", sortable: true },
      { field: CommoditiesColumn.NAME, headerName: "Name", sortable: true },
      {
        field: CommoditiesColumn.NAME_IT,
        headerName: "Name IT",
        sortable: true,
        sortField: "name_it",
      },
      {
        field: CommoditiesColumn.IS_GROUP,
        headerName: "Group Name",
        sortable: true,
        path: "parent.name_it",
        sortField: "parent_id",
      },
      {
        field: CommoditiesColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "name_it",
        label: "Name IT",
        control: "textfield",
        required: true,
      },
      {
        field: "parent",
        label: "Commodity Group",
        control: "dynamic",
        api: "/api/commodities?parent_id__isnull=true",
        path: "parent.name_it",
        mapper: "commodity",
        size: 500,
        filter: false,
        required: true,
      },
      {
        field: "parent_id",
        label: "Commodity Group",
        control: "dynamic",
        api: "/api/commodities?parent_id__isnull=true",
        mapper: "commodity",
        size: 500,
        view: false,
        create: false,
      },
    ],
  },
  commodity_groups: {
    key: "commodity_groups",
    label: "Commodity Groups",
    item: "Commodity Group",
    path: "commodity-groups",
    endpoints: {
      list: "/api/commodities?show_all=true&parent_id__isnull=true",
      getOne: (id) => `/api/commodities/${id}`,
      create: "/api/commodities",
      update: (id) => `/api/commodities/${id}`,
      delete: (id) => `/api/commodities/${id}`,
      restore: (id) => `/api/commodities/${id}/restore`,
    },
    columns: [
      { field: CommoditiesColumn.ID, headerName: "Id", sortable: true },
      { field: CommoditiesColumn.NAME, headerName: "Name", sortable: true },
      {
        field: CommoditiesColumn.NAME_IT,
        headerName: "Name IT",
        sortable: true,
        sortField: "name_it",
      },
      {
        field: CommoditiesColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "name_it",
        label: "Name IT",
        control: "textfield",
        required: true,
      },
    ],
  },
  terminals: {
    key: "terminals",
    label: "Terminals",
    item: "Terminal",
    path: "terminals",
    cellWidth: 250,
    endpoints: {
      list: "/api/terminals?show_all=true",
      getOne: (id) => `/api/terminals/${id}`,
      create: "/api/terminals",
      update: (id) => `/api/terminals/${id}`,
      delete: (id) => `/api/terminals/${id}`,
      restore: (id) => `/api/terminals/${id}/restore`,
    },
    columns: [
      { field: TerminalsColumn.ID, headerName: "Id", sortable: true },
      {
        field: TerminalsColumn.PORT_NAME,
        headerName: "Port",
        sortable: true,
        sortField: "port__name",
        path: "port.name",
      },
      {
        field: TerminalsColumn.SUPPLIER,
        headerName: "Supplier",
        sortable: true,
        sortField: "supplier__name",
        path: "supplier.name",
      },
      { field: TerminalsColumn.NAME, headerName: "Name", sortable: true },
      {
        field: TerminalsColumn.FISCAL_WAREHOUSE,
        headerName: "Fiscal Warehouse",
      },
      {
        field: TerminalsColumn.FISCAL_WAREHOUSE_CODE,
        headerName: "Fiscal Warehouse Code",
        sortable: true,
      },
      {
        field: TerminalsColumn.BONDED_WAREHOUSE,
        headerName: "Bonded Warehouse",
      },
      {
        field: TerminalsColumn.BONDED_WAREHOUSE_CODE,
        headerName: "Bonded Warehouse Code",
        sortable: true,
      },
      {
        field: TerminalsColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "port",
        label: "Port",
        control: "dynamic",
        api: "/api/ports",
        path: "port.name",
        filter: false,
        required: true,
      },
      {
        field: "port",
        label: "Port",
        control: "dynamic",
        api: "/api/ports?show_all=true",
        create: false,
        view: false,
        multi: true,
      },
      {
        field: "supplier",
        label: "Supplier",
        control: "dynamic",
        api: "api/suppliers/names",
        path: "supplier.name",
        filter: false,
      },
      {
        field: "supplier",
        label: "Supplier",
        control: "dynamic",
        api: "api/suppliers/names?show_all=true",
        create: false,
        view: false,
        multi: true,
      },
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "fiscal_warehouse_code",
        label: "Fiscal Warehouse Code",
        control: "textfield",
        dependency: "fiscal_warehouse",
      },
      {
        field: "bonded_warehouse_code",
        label: "Bonded Warehouse Code",
        control: "textfield",
        dependency: "fiscal_warehouse",
      },
    ],
  },
  truck: {
    key: "truck",
    label: "Kind of Truck",
    path: "truck-kinds",
    endpoints: {
      list: "/api/truck-kinds?show_all=true",
      getOne: (id) => `/api/truck-kinds/${id}`,
      create: "/api/truck-kinds",
      update: (id) => `/api/truck-kinds/${id}`,
      delete: (id) => `/api/truck-kinds/${id}`,
      restore: (id) => `/api/truck-kinds/${id}/restore`,
    },
    columns: [
      { field: TruckColumn.ID, headerName: "Id", sortable: true },
      { field: TruckColumn.NAME, headerName: "Name", sortable: true },
      {
        field: TruckColumn.NAME_IT,
        headerName: "Name IT",
        sortable: true,
        sortField: "name_it",
      },
      {
        field: TruckColumn.SERVICE,
        headerName: "Service",
        path: "service.description",
        sortable: true,
        sortField: "service__description",
      },
      {
        field: TruckColumn.MACRO_CATEGORY,
        headerName: "Macro Category",
        sortable: true,
        sortField: "macro_category",
      },
      {
        field: TruckColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "name_it",
        label: "Name IT",
        control: "textfield",
        required: true,
      },
      {
        field: "service",
        label: "Service",
        control: "dynamic",
        api: "/api/services?route__in=DELIVERY",
        mapper: "service",
        option: "description",
        multi: true,
        create: false,
        view: false,
        applyCleaning: true,
      },
      {
        field: "service",
        label: "Service",
        control: "dynamic",
        api: "/api/services?route__in=DELIVERY",
        mapper: "service",
        path: "service.description",
        option: "description",
        filter: false,
        applyCleaning: true,
      },
      {
        field: "macro_category",
        label: "Macro Category",
        control: "autocomplete",
        options: MACRO_CATEGORY,
      },
    ],
  },
  ports: {
    key: "ports",
    label: "Ports",
    item: "Port",
    path: "ports",
    endpoints: {
      list: "/api/ports?show_all=true",
      getOne: (id) => `/api/ports/${id}`,
      create: "/api/ports",
      update: (id) => `/api/ports/${id}`,
      delete: (id) => `/api/ports/${id}`,
      restore: (id) => `/api/ports/${id}/restore`,
    },
    columns: [
      { field: PortsColumn.ID, headerName: "Id", sortable: true },
      { field: PortsColumn.NAME, headerName: "Name", sortable: true },
      { field: PortsColumn.COUNTRY, headerName: "Country", sortable: true },
      {
        field: PortsColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "country",
        label: "Country",
        control: "autocomplete",
        options: COUNTRIES,
        required: true,
      },
    ],
  },
  terms: {
    key: "terms",
    label: "Terms of Payment",
    path: "terms-of-payment",
    endpoints: {
      list: "/api/terms-of-payment?show_all=true",
      getOne: (id) => `/api/terms-of-payment/${id}`,
      create: "/api/terms-of-payment",
      update: (id) => `/api/terms-of-payment/${id}`,
      delete: (id) => `/api/terms-of-payment/${id}`,
      restore: (id) => `/api/terms-of-payment/${id}/restore`,
    },
    columns: [
      {
        field: PaymentTermsColumn.ID,
        headerName: "Id",
        sortable: true,
      },
      { field: PaymentTermsColumn.NAME, headerName: "Name", sortable: true },
      {
        field: PaymentTermsColumn.NAME_IT,
        headerName: "Name IT",
        sortable: true,
      },
      {
        field: PaymentTermsColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "name_it",
        label: "Name IT",
        control: "textfield",
        required: true,
      },
    ],
  },
  packages: {
    key: "packages",
    label: "Macro Services",
    path: "macro-services",
    endpoints: {
      list: "/api/packages?show_all=true",
      getOne: (id) => `/api/packages/${id}`,
      create: "/api/packages",
      update: (id) => `/api/packages/${id}`,
      delete: (id) => `/api/packages/${id}`,
      restore: (id) => `/api/packages/${id}/restore`,
    },
    columns: [
      {
        field: PackageColumn.ID,
        headerName: "Id",
        sortable: true,
      },
      { field: PackageColumn.NAME, headerName: "Name", sortable: true },
      {
        field: PackageColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "name",
        label: "Name",
        control: "textfield",
        required: true,
        filter: true,
      },
    ],
  },
  suppliers: {
    key: "suppliers",
    label: "Suppliers",
    item: "Supplier",
    path: "suppliers",
    cellWidth: 250,
    endpoints: {
      list: "/api/suppliers?show_all=true",
      getOne: (id) => `/api/suppliers/${id}`,
      create: "/api/suppliers",
      update: (id) => `/api/suppliers/${id}`,
      delete: (id) => `/api/suppliers/${id}`,
      restore: (id) => `/api/suppliers/${id}/restore`,
    },
    columns: [
      { field: SuppliersColumn.ID, headerName: "Id", sortable: true },
      {
        field: SuppliersColumn.COMPANY_TYPE_OVERLAP,
        headerName: "Company Type",
      },
      { field: SuppliersColumn.NAME, headerName: "Name", sortable: true },
      {
        field: SuppliersColumn.ADDRESS,
        headerName: "Legal Address",
        sortable: true,
      },
      {
        field: SuppliersColumn.POST_CODE,
        headerName: "Legal Address Post Code",
        sortable: true,
        sortField: "city__post_code",
        path: "city.post_code",
      },
      {
        field: SuppliersColumn.CITY,
        headerName: "Legal Address City",
        sortable: true,
        sortField: "city__name",
        path: "city.name",
      },
      {
        field: SuppliersColumn.PROVINCE,
        headerName: "Legal Address Province",
        sortable: true,
        sortField: "city__province",
        path: "city.province",
      },
      {
        field: SuppliersColumn.LEGAL_COUNTRY,
        headerName: "Legal Address Country",
        sortable: true,
        sortField: "city__country",
        path: "city.country",
      },
      {
        field: SuppliersColumn.EMAIL,
        headerName: "Email",
        sortable: true,
      },
      {
        field: SuppliersColumn.TERMS,
        headerName: "Terms of Payment",
        sortable: true,
        sortField: "terms_of_payment__name",
        path: "terms_of_payment.name",
      },
      {
        field: SuppliersColumn.VAT_NUMBER,
        headerName: "Vat Number",
        sortable: true,
      },
      {
        field: SuppliersColumn.NOTE,
        headerName: "Note",
        sortable: true,
      },
      {
        field: SuppliersColumn.TRANSPORTER_REGISTRY_NO,
        headerName: "Transporter Registry No",
        sortable: true,
      },
      {
        field: SuppliersColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "company_type",
        multi: true,
        label: "Company Type",
        control: "autocomplete",
        options: COMPANY_TYPE_OVERLAP,
        required: true,
      },
      { field: "name", label: "Name", control: "textfield", required: true },
      {
        field: "address",
        label: "Legal Address",
        control: "textfield",
        required: true,
      },
      {
        field: "post_code",
        label: "Legal Address Post code",
        control: "textfield",
        path: "city.post_code",
        filter: false,
        create: false,
      },
      {
        field: "city__post_code",
        label: "Legal Address Post Code",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "city",
        label: "Legal Address City",
        control: "dynamic",
        api: "/api/cities",
        path: "city.name",
        required: true,
        filter: false,
      },
      {
        field: "city__name",
        label: "Legal Address City",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "province",
        label: "Legal Address Province",
        control: "textfield",
        path: "city.province",
        filter: false,
        create: false,
      },
      {
        field: "city__province",
        label: "Legal Address Province",
        control: "textfield",
        create: false,
        view: false,
      },
      {
        field: "country",
        label: "Legal Address Country",
        control: "textfield",
        path: "city.country",
        filter: false,
        create: false,
      },
      {
        field: "city__country",
        label: "Legal Address Country",
        control: "autocomplete",
        options: COUNTRIES,
        create: false,
        view: false,
      },
      {
        field: "email",
        label: "Email",
        control: "textfield",
        validation: ["email"],
      },
      {
        field: "vat_number",
        label: "Vat Number",
        control: "textfield",
      },
      {
        field: "note",
        label: "Note",
        control: "textfield",
      },
      {
        field: "transporter_registry_no",
        label: "Transport Registry No",
        control: "textfield",
      },
      {
        field: "terms_of_payment",
        label: "Terms of Payment",
        control: "dynamic",
        multi: true,
        api: "/api/terms-of-payment?show_all=true",
        create: false,
        view: false,
      },
      {
        field: "terms_of_payment",
        label: "Terms of Payment",
        control: "dynamic",
        api: "/api/terms-of-payment",
        path: "terms_of_payment.name",
        filter: false,
      },
    ],
  },
  services: {
    key: "services",
    label: "Shipping Case Services",
    item: "Shipping Case Service",
    path: "shipping-case-services",
    endpoints: {
      list: "/api/activities?show_all=true",
      getOne: (id) => `/api/activities/${id}`,
      create: "/api/activities",
      update: (id) => `/api/activities/${id}`,
      delete: (id) => `/api/activities/${id}`,
      restore: (id) => `/api/activities/${id}/restore`,
    },
    columns: [
      { field: ServicesColumns.ID, headerName: "Id", sortable: true },
      {
        field: ServicesColumns.FLOW_OF_GOODS,
        headerName: "Flow of goods",
        sortable: true,
      },
      { field: ServicesColumns.ROUTE, headerName: "Route", sortable: true },
      {
        field: ServicesColumns.DESCRIPTION,
        headerName: "Description",
        sortable: true,
      },
      {
        field: ServicesColumns.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "flow_of_goods",
        label: "Flow of goods",
        control: "autocomplete",
        options: FLOW_OF_GOODS,
        required: true,
      },
      {
        field: "route",
        label: "Route",
        control: "autocomplete",
        options: ROUTES,
        required: true,
      },
      {
        field: "description",
        label: "Description",
        control: "textfield",
        required: true,
      },
    ],
  },
  production_method: {
    key: "production_method",
    label: "Production Methods",
    path: "production-methods",
    endpoints: {
      list: "/api/production-methods?show_all=true",
      getOne: (id) => `/api/production-methods/${id}`,
      create: "/api/production-methods",
      update: (id) => `/api/production-methods/${id}`,
      delete: (id) => `/api/production-methods/${id}`,
      restore: (id) => `/api/production-methods/${id}/restore`,
    },
    columns: [
      {
        field: ProductionMethodColumn.ID,
        headerName: "Id",
        sortable: true,
      },
      {
        field: ProductionMethodColumn.NAME,
        headerName: "Name",
        sortable: true,
      },
      {
        field: ProductionMethodColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      { field: "name", label: "Name", control: "textfield", required: true },
    ],
  },
  service_master: {
    key: "service_master",
    label: "Service Master",
    item: "Service Master",
    path: "service-master",
    cellWidth: 250,
    endpoints: {
      list: "/api/services?show_all=true",
      getOne: (id) => `/api/services/${id}`,
      create: "/api/services",
      update: (id) => `/api/services/${id}`,
      delete: (id) => `/api/services/${id}`,
      restore: (id) => `/api/services/${id}/restore`,
    },
    columns: [
      { field: ServiceMasterColumn.ID, headerName: "Id", sortable: true },
      { field: ServiceMasterColumn.ROUTE, headerName: "Route", sortable: true },
      {
        field: ServiceMasterColumn.TYPE,
        headerName: "Service Type",
        sortable: true,
      },
      {
        field: ServiceMasterColumn.FLOW_OF_GOODS,
        headerName: "Flow of goods",
        sortable: true,
      },
      {
        field: ServiceMasterColumn.DESCRIPTION,
        headerName: "Description",
        sortable: true,
      },
      {
        field: ServiceMasterColumn.DESCRIPTION_IT,
        headerName: "Description IT",
        sortable: true,
        sortField: "description_it",
      },
      {
        field: ServiceMasterColumn.COMMENT,
        headerName: "Note",
        sortable: true,
      },
      {
        field: ServiceMasterColumn.PRICE_ACCESS_CONTROL,
        headerName: "Price Access Control",
      },
      {
        field: ServiceMasterColumn.DELETED,
        headerName: "Deleted",
        sortable: true,
        sortField: "deleted_at",
      },
    ],
    fields: [
      {
        field: "route",
        label: "Route",
        control: "autocomplete",
        options: ROUTES,
        required: true,
        filter: false,
      },
      {
        field: "route",
        label: "Route",
        control: "autocomplete",
        options: ROUTES,
        view: false,
        create: false,
        multi: true,
      },
      {
        field: "type",
        label: "Service Type",
        control: "autocomplete",
        options: SERVICE_TYPE,
        required: true,
      },
      {
        field: "flow_of_goods",
        label: "Flow of goods",
        control: "autocomplete",
        options: FLOW_OF_GOODS,
        required: true,
      },
      {
        field: "description",
        label: "Description",
        control: "textfield",
        required: true,
      },
      {
        field: "description_it",
        label: "Description IT",
        control: "textfield",
        required: true,
      },
      {
        field: "comment",
        label: "Notes",
        control: "textfield",
      },
      {
        field: "price_access_control",
        label: "Price Access Control",
        control: "autocomplete",
        options: ROLE,
        required: true,
        multi: true,
        filter: false,
      },
    ],
  },

  apl: APL_ENTITY_CONFIG,
  ppl: PPL_ENTITY_CONFIG,
};

export function getEntityConfig<K extends SettingsEntityKey>(
  key: K,
): SettingsEntityConfigByKey<K> {
  return SETTINGS_ENTITIES[key];
}
